import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';

import { breakAdd, breakUpdate } from '../../../store/slices/tenantSlices/configurationSlice';
import { processAllOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import FormDateInput from '../../../components/form/FormDateInput';
import { format, parse, startOfDay } from 'date-fns';
import { useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';

const AddEditBreakModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();
  const [processOptions, setProcessOptions] = useState([]);
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);
  const initialValues = {
    name: '',
    description: '',
    process_id: '',
    active: 'true',
    duration: startOfDay(today)
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('name is required'),
    description: Yup.string().required('description is required'),
    process_id: Yup.string().required('process is required'),
    duration: Yup.string().required('duration is required')
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const formattedDuration = format(new Date(values.duration), 'HH:mm');
        const payload = { ...values, id: editRecord.id };
        payload['duration'] = formattedDuration;
        const response = await dispatch(breakUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const formattedDuration = format(new Date(values.duration), 'HH:mm');
        const payload = { ...values };
        payload['duration'] = formattedDuration;
        const response = await dispatch(breakAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getProcessOption();
  }, []);

  useEffect(() => {
    if (isEdit) {
      const duration = parse(editRecord?.duration, 'HH:mm', today);
      setValues({
        ...values,
        name: editRecord?.name,
        description: editRecord?.description,
        duration: duration,
        active: String(editRecord?.active),
        process_id: Number(editRecord?.process_id)
      });
    }
  }, [editRecord]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Break' : 'Add Break'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="description"
                label="description"
                placeholder="Enter Description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>

            <Col lg="3">
              <FormToggle
                name="active"
                label="Active"
                value={values.active}
                onChange={(e) => setFieldValue('active', e)}
              />
            </Col>
            <Col lg="3">
              <FormDateInput
                dateFormat="HH:mm"
                timeFormat="HH:mm"
                label="Duration (HH:MM)"
                showTimeSelect={true}
                name="duration"
                value={values.duration}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                showTimeSelectOnly={true}
                timeIntervals="5"
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="process_id"
                label="Process"
                placeholder="Select Process"
                value={values.process_id}
                options={processOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                menuPlacement="bottom"
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditBreakModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditBreakModal;
